import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import last from 'lodash/last';
import { useCookies } from 'react-cookie';
import Tour, { RequestStatus, StyledTour } from './Tour';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh.hook';
import { checkForSolution, getSolution, setError, setSolutionParam } from '../../../actions';
import { hasOngoingAsyncRequest, isIterationSolution } from '../../../utils/SolutionHelpers';
import withTranslation from '../../../hoc/withTranslation';
import { isProdEnv } from '../../../utils/helpers';
import config from '../../../config';
import { TOP_BAR_HEIGHT } from '../../TopBar/TopBar';

const { tpaVersions } = config;

const isProd = isProdEnv();
const DESKTOP_PANEL_TOP_OFFSET = `calc(${TOP_BAR_HEIGHT} + 1rem)`;
const DESKTOP_PANEL_RIGHT_OFFSET = '4.5rem';

// TODO adjust for mobile
const StyledTourPanel = styled.div(({ isOpen, jsonOpen, hasContent }) => ({
  display: isOpen ? 'flex' : 'none',
  position: 'fixed',
  flexDirection: 'column',
  gap: '0.25rem',
  right: jsonOpen ? '27rem' : DESKTOP_PANEL_RIGHT_OFFSET,
  top: DESKTOP_PANEL_TOP_OFFSET,
  backgroundColor: 'rgba(255,255,255,0.95)',
  zIndex: '999',
  boxShadow: '0px 10px 29px 0px rgba(0, 0, 0, 0.22)',
  borderRadius: '0.25rem',
  backdropFilter: 'blur(10px)',
  textRendering: 'geometricPrecision',
  borderRight: jsonOpen && '3px solid grey',
  overflowY: 'auto',
  overflowX: 'hidden',
  maxHeight: '100%',
  transition: 'all 0.5s cubic-bezier(.72,0,0,.99)',
  padding: '0.25rem',
  '@media screen and (max-width: 850px)': {
    display: hasContent && isOpen ? 'flex' : 'none',
    top: '3rem',
    bottom: 'auto',
    left: 0,
    right: 0,
    height: 'auto',
    maxHeight: 'calc(50% - 3rem)',
    width: '100%',
    borderRadius: '0 0 0.5rem 0.5rem',
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.15)',
    zIndex: '899',
  },
}));

const TourPanel = ({
  solution,
  user,
  oAuth,
  isOpen,
  translations: {
    solutionsStatusPanel: { noSolutionsToShow },
  },
}) => {
  const dispatch = useDispatch();
  const [refresh] = useAutoRefresh(5000);
  const handleSetSolutionParameter = useCallback(
    (data) => dispatch(setSolutionParam(data)),
    [dispatch],
  );
  const tourPlanner = useSelector(
    ({ tourPlanner: stateTourPlanner }) => stateTourPlanner.value[solution.show],
  );
  const orders = useSelector((state) => state.orders[solution.show]);

  const [solutionsToShow, setSolutionsToShow] = useState(null);
  const [cookies] = useCookies(['apikey']);
  const ongoingRequests = hasOngoingAsyncRequest(solution.requests);
  const hasOrders = orders.length > 0 || solution.requests[solution.show]?.tempOrders?.length > 0;

  useEffect(() => {
    const filteredSolutions = solution.requests.filter((request) => {
      if (request.status === 'canceling...') {
        return false;
      }

      const lastIteration = last(request.iterations);
      const fileName = lastIteration?.request?.filename || request?.name;

      const response =
        fileName &&
        ((isIterationSolution(lastIteration) && lastIteration.response) ||
          lastIteration?.request?.index !== undefined);
      return response || request.status;
    });
    setSolutionsToShow(filteredSolutions);
  }, [solution]);

  useEffect(() => {
    if ((!isProd && !oAuth) || (isProd && !cookies)) return;
    solution.requests.forEach((request, index) => {
      const tpaVersion = user.tpaVersion;
      const versionApiKey = tpaVersions[tpaVersion.version]?.apiKey;
      if (
        request.statusHref &&
        (!request.status || request.status === 'pending' || request.status === 'inProgress')
      ) {
        checkForSolution({
          href: request.statusHref,
          oAuth,
          apiKey: versionApiKey || (isProd && cookies.apikey),
          dispatch,
          index,
          status: request.status,
          isActive: solution.show === index,
          keepOrders: tourPlanner.ordersMode !== 'json_import',
          isDemo: user.isLastOrdersAddedDemo || tourPlanner.ordersMode === 'demo',
        });
      }
    });
  }, [refresh]);

  useEffect(() => {
    solution.requests.forEach((req) => {
      const status = req.status;
      if (status === RequestStatus.PENDING && !req.asyncStartTime) {
        handleSetSolutionParameter({
          param: { asyncStartTime: Math.floor(Date.now() / 1000) },
          index: solution.show,
        });
      }
      if (status === RequestStatus.FAILURE) {
        dispatch(setError(req.status));
      }

      if (status === RequestStatus.IN_PROGRESS && !req.runtimeStart && !req.runtime) {
        handleSetSolutionParameter({
          param: { runtimeStart: Date.now() },
          index: solution.show,
        });
      }
      if (status === RequestStatus.SUCCESS && req.runtimeStart) {
        const runtimeFinishTime = Date.now();
        handleSetSolutionParameter({
          param: { runtime: runtimeFinishTime - req.runtimeStart, runtimeFinishTime },
          index: solution.show,
        });
      }
    });
  }, [solution.requests, solution.show, handleSetSolutionParameter]);

  useEffect(() => {
    if ((!isProd && !oAuth) || (isProd && !cookies)) return;
    const tpaVersion = user.tpaVersion;
    const versionApiKey = tpaVersions[tpaVersion.version]?.apiKey;
    solution.requests.forEach((request, index) => {
      if (request.solutionHref) {
        getSolution({
          href: request.solutionHref,
          oAuth,
          apiKey: versionApiKey || (isProd && cookies.apikey),
          index,
          dispatch,
          keepOrders: tourPlanner.ordersMode !== 'json_import',
        });
      }
    });
  }, [oAuth]);

  return (
    <StyledTourPanel
      data-test-id="JVFilename"
      isOpen={isOpen}
      jsonOpen={user.jsonPanelOpen}
      hasContent={solutionsToShow && solutionsToShow.length > 0 && hasOrders}
    >
      {solutionsToShow && solutionsToShow.length > 0 && hasOrders ? (
        solutionsToShow.map((request, index) => (
          <Tour
            key={`solution-element-${index}`}
            request={request}
            isSelected={solution.show === index}
            index={index}
            tpaVersion={user.tpaVersion}
            apiKey={cookies.apikey}
            oAuth={oAuth}
            isRememberLastState={user.hasStateMemory}
            isProdEnv={isProd}
            hasOngoingRequests={ongoingRequests}
          />
        ))
      ) : (
        <StyledTour noHover>{noSolutionsToShow}</StyledTour>
      )}
    </StyledTourPanel>
  );
};

export default withTranslation(TourPanel);
